import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Check, ChevronLeft, ChevronRight,
  Globe, Layers, Loader2, MapPin, Package,
  ShoppingBag, ShoppingCart, Store, Shield, AlertCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useActiveChannel } from '@/store/activeChannel';
import { persistTenantId, useTenant } from '@/hooks/useTenant';
import { validateName, validateDocument } from '@/lib/validators';
import { Logo } from '@/components/ui/Logo';
import { cn } from '@/lib/utils';

type ChannelKind = 'mercado_livre' | 'shopee' | 'shopify' | 'site_proprio' | 'atacado' | 'loja_fisica' | 'amazon' | 'outro';

const KINDS: Array<{ kind: ChannelKind; label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = [
  { kind: 'mercado_livre', label: 'Mercado Livre', icon: ShoppingBag,  color: '#ffe600' },
  { kind: 'shopee',        label: 'Shopee',        icon: ShoppingCart, color: '#ee4d2d' },
  { kind: 'shopify',       label: 'Shopify',       icon: Store,        color: '#96bf48' },
  { kind: 'site_proprio',  label: 'Site próprio',  icon: Globe,        color: '#3b82f6' },
  { kind: 'atacado',       label: 'Atacado / B2B', icon: Building2,    color: '#8b5cf6' },
  { kind: 'loja_fisica',   label: 'Loja física',   icon: MapPin,       color: '#f59e0b' },
  { kind: 'amazon',        label: 'Amazon',        icon: Package,      color: '#ff9900' },
  { kind: 'outro',         label: 'Outro',         icon: Layers,       color: '#64748b' },
];

const TERMS_VERSION = '1.0';
const STEPS = ['Sua empresa', 'Tipo de loja', 'Consentimento'];

export function OnboardingChannel() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const { setActiveChannel } = useActiveChannel();

  // Usuário que já completou o onboarding não deve ver esta tela novamente
  useEffect(() => {
    if (tenantId) window.location.replace('/farms');
  }, [tenantId]);

  const [step, setStep]       = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // "attempted" torna os erros de campo visíveis ao tentar avançar sem preencher
  const [attempted, setAttempted] = useState(false);

  // Passo 1 — Dados da empresa
  const [companyName, setCompanyName] = useState('');
  const [document, setDocument]       = useState('');

  // Passo 2 — Canal principal
  const [selectedKind, setSelectedKind] = useState<ChannelKind | null>(null);

  // Passo 3 — Consentimento
  const [termsAccepted, setTermsAccepted]     = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn]   = useState(false);

  // ── Validações ────────────────────────────────────────────────────────────────
  const companyNameEmpty = companyName.trim().length === 0;
  const companyNameShort = companyName.trim().length > 0 && companyName.trim().length < 2;
  const companyNameErr   = companyName.trim().length >= 2
    ? validateName(companyName, { label: 'Nome da empresa', min: 2, max: 120 })
    : null;
  const documentErr = document.trim() ? validateDocument(document) : null;

  const canProceed = [
    companyName.trim().length >= 2 && !companyNameErr && !documentErr,
    selectedKind !== null,
    termsAccepted && privacyAccepted,
  ];

  // ── Avançar passo com validação visível ───────────────────────────────────────
  function tryAdvance() {
    if (!canProceed[step]) {
      setAttempted(true);
      return;
    }
    setAttempted(false);
    setStep((s) => s + 1);
  }

  // ── Submissão final ───────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!user || !termsAccepted || !privacyAccepted) return;
    setLoading(true);
    setError('');

    try {
      const now  = new Date().toISOString();
      const slug = companyName.toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        + '-' + Date.now();

      // 1. Cria tenant + owner + categorias padrão atomicamente via RPC SECURITY DEFINER
      const { data: newTenantId, error: tenantErr } = await supabase
        .rpc('create_tenant' as never, { p_name: companyName.trim(), p_slug: slug } as never);

      if (tenantErr || !newTenantId) {
        throw new Error(`Falha ao criar empresa: ${(tenantErr as any)?.message ?? 'sem resposta do servidor'}`);
      }
      const tid = newTenantId as string;

      // Cache local imediato — evita race condition com TenantGuard
      persistTenantId(user.id, tid);

      // 2. Salva campos de consentimento LGPD (falha silenciosa se coluna não existir)
      void supabase
        .from('tenants')
        .update({
          document:            document.replace(/\D/g, '') || null,
          terms_accepted_at:   now,
          terms_version:       TERMS_VERSION,
          privacy_accepted_at: now,
          marketing_opt_in:    marketingOptIn,
        } as never)
        .eq('id', tid);

      // 3. Cria canal "Geral" (padrão). ON CONFLICT é tratado via upsert implícito:
      //    se já existir (UNIQUE tenant_id+name) ignoramos o erro.
      const { error: geralErr } = await supabase
        .from('channels')
        .insert({ tenant_id: tid, name: 'Geral', kind: 'outro', is_default: true } as never);

      if (geralErr && !geralErr.message.includes('duplicate') && !geralErr.message.includes('already exists')) {
        // Erro inesperado — logar mas não bloquear (canal pode ser criado depois)
        console.warn('[Onboarding] Canal Geral:', geralErr.message);
      }

      // 4. Cria canal do tipo escolhido pelo usuário
      const kindMeta = KINDS.find((k) => k.kind === selectedKind)!;
      const { data: ch, error: kindChErr } = await supabase
        .from('channels')
        .insert({ tenant_id: tid, name: kindMeta.label, kind: selectedKind, color: kindMeta.color } as never)
        .select('id')
        .single();

      if (kindChErr && !kindChErr.message.includes('duplicate')) {
        console.warn('[Onboarding] Canal de tipo:', kindChErr.message);
      }

      if (ch) setActiveChannel((ch as { id: string }).id);

      // Hard redirect para garantir que o useTenant() relê o banco com o novo tenant
      window.location.href = '/farms';

    } catch (e: any) {
      const raw = (e.message ?? '') as string;
      // Não expõe detalhes técnicos (SQL, stack trace) ao usuário
      if (raw.includes('Auth required') || raw.includes('JWT')) {
        setError('Sessão expirada. Faça login novamente.');
      } else if (raw.includes('duplicate') || raw.includes('already exists') || raw.includes('unique')) {
        setError('Esta empresa já foi cadastrada. Faça login para acessá-la.');
      } else {
        setError('Não foi possível criar sua empresa. Verifique sua conexão e tente novamente.');
      }
      setLoading(false);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center bg-surface p-4">
      <div className="w-full max-w-lg">

        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo size={36} />
        </div>

        {/* Barra de progresso */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors',
                i < step    ? 'bg-positive text-white'
                : i === step ? 'bg-brand-700 text-white'
                : 'bg-surface-subtle text-ink-muted'
              )}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={cn('text-xs hidden sm:block truncate',
                i === step ? 'text-ink font-medium' : 'text-ink-muted')}>
                {s}
              </span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
            </div>
          ))}
        </div>

        <div className="card p-8">

          {/* ── Passo 1: Dados da empresa ──────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h1 className="font-display font-bold text-xl text-ink">Bem-vindo ao MYND BPO</h1>
                <p className="text-sm text-ink-muted mt-1">Vamos configurar sua empresa. Leva menos de 2 minutos.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">
                  Nome da empresa <span className="text-negative">*</span>
                </label>
                <input
                  type="text"
                  autoFocus
                  maxLength={120}
                  className={cn(
                    'input w-full',
                    (attempted && (companyNameEmpty || companyNameShort)) && 'border-negative',
                    companyNameErr && !companyNameEmpty && !companyNameShort && 'border-negative',
                  )}
                  placeholder="ex: Minha Loja LTDA"
                  value={companyName}
                  onChange={(e) => { setCompanyName(e.target.value); setAttempted(false); }}
                />
                {/* Erros forçados (ao tentar avançar) */}
                {attempted && companyNameEmpty && (
                  <p className="text-xs text-negative mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />Nome da empresa é obrigatório.
                  </p>
                )}
                {attempted && companyNameShort && (
                  <p className="text-xs text-negative mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />Nome deve ter pelo menos 2 caracteres.
                  </p>
                )}
                {/* Validação em tempo real (injeção, caracteres inválidos) */}
                {companyNameErr && !companyNameEmpty && !companyNameShort && (
                  <p className="text-xs text-negative mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />{companyNameErr}
                  </p>
                )}
                {/* Feedback positivo quando nome válido */}
                {!companyNameErr && companyName.trim().length >= 2 && (
                  <p className="text-xs text-positive mt-1 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" />Nome válido.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">
                  CNPJ / CPF <span className="text-ink-muted font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  maxLength={18}
                  className={cn('input w-full', documentErr && 'border-negative')}
                  placeholder="00.000.000/0001-00"
                  value={document}
                  onChange={(e) => setDocument(e.target.value)}
                />
                {documentErr && (
                  <p className="text-xs text-negative mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />{documentErr}
                  </p>
                )}
                <p className="text-xs text-ink-soft mt-1">
                  Usado para identificação e emissão de documentos fiscais futuros.
                </p>
              </div>
            </div>
          )}

          {/* ── Passo 2: Tipo de loja ──────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-semibold text-lg text-ink">Principal canal de vendas</h2>
                <p className="text-sm text-ink-muted mt-1">Selecione onde você vende. Pode adicionar outros canais depois.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {KINDS.map(({ kind, label, icon: Icon, color }) => {
                  const active = selectedKind === kind;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => { setSelectedKind(kind); setAttempted(false); }}
                      className={cn(
                        'flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-xs font-medium',
                        active
                          ? 'border-brand-500 bg-brand-50 text-brand-800 shadow-sm'
                          : 'border-border hover:border-brand-300 text-ink-muted hover:text-ink'
                      )}
                    >
                      <span style={{ color: active ? undefined : color }}>
                        <Icon className="w-6 h-6" />
                      </span>
                      {label}
                      {active && <Check className="w-3 h-3 text-brand-600" />}
                    </button>
                  );
                })}
              </div>
              {attempted && !selectedKind && (
                <p className="text-xs text-negative flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />Selecione pelo menos um canal de vendas.
                </p>
              )}
            </div>
          )}

          {/* ── Passo 3: Consentimento ─────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-semibold text-lg text-ink flex items-center gap-2">
                  <Shield className="w-5 h-5 text-brand-700" />
                  Termos e privacidade
                </h2>
                <p className="text-sm text-ink-muted mt-1">
                  Leia e aceite os documentos abaixo para concluir o cadastro.
                </p>
              </div>

              {/* Resumo LGPD */}
              <div className="rounded-xl border border-border bg-surface-subtle/50 p-4 text-xs text-ink-muted space-y-2">
                <p className="font-semibold text-ink text-sm">Como usamos seus dados (LGPD)</p>
                <ul className="space-y-1 list-disc pl-4">
                  <li>Seus dados são usados exclusivamente para operar e melhorar a plataforma.</li>
                  <li>Dados financeiros ficam armazenados de forma criptografada e isolados por empresa (RLS).</li>
                  <li>Não vendemos nem cedemos seus dados a anunciantes.</li>
                  <li>Solicite acesso, correção ou exclusão em <strong>bemyndstore@gmail.com</strong>.</li>
                </ul>
              </div>

              <div className="space-y-3">
                {/* Termos de uso */}
                <label className={cn(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors',
                  termsAccepted ? 'border-brand-400 bg-brand-50/50' : 'border-border hover:border-brand-200'
                )}>
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 rounded shrink-0 accent-brand-700"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <span className="text-sm text-ink leading-snug">
                    Li e aceito os{' '}
                    <Link to="/farms/termos" target="_blank" className="text-brand-700 underline font-medium">
                      Termos de Uso
                    </Link>{' '}
                    da plataforma MYND BPO.{' '}
                    <span className="text-negative text-xs font-medium">Obrigatório</span>
                  </span>
                </label>

                {/* Política de privacidade */}
                <label className={cn(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors',
                  privacyAccepted ? 'border-brand-400 bg-brand-50/50' : 'border-border hover:border-brand-200'
                )}>
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 rounded shrink-0 accent-brand-700"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  />
                  <span className="text-sm text-ink leading-snug">
                    Li e concordo com a{' '}
                    <Link to="/privacidade" target="_blank" className="text-brand-700 underline font-medium">
                      Política de Privacidade
                    </Link>{' '}
                    e com o tratamento dos meus dados conforme a LGPD.{' '}
                    <span className="text-negative text-xs font-medium">Obrigatório</span>
                  </span>
                </label>

                {/* Marketing — opcional */}
                <label className="flex items-start gap-3 p-4 rounded-xl border border-border hover:bg-surface-subtle/50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 rounded shrink-0 accent-brand-700"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                  />
                  <span className="text-sm text-ink-muted leading-snug">
                    Quero receber novidades e atualizações por e-mail.{' '}
                    <span className="text-ink-soft text-xs">Opcional</span>
                  </span>
                </label>
              </div>

              {attempted && (!termsAccepted || !privacyAccepted) && (
                <p className="text-xs text-negative flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />Aceite os termos obrigatórios para continuar.
                </p>
              )}

              {error && (
                <div role="alert" className="text-sm text-negative rounded-xl bg-negative/5 border border-negative/20 p-3">
                  {error}
                </div>
              )}

              <p className="text-xs text-ink-soft">
                Ao concluir, registramos o aceite com data/hora e versão dos documentos ({TERMS_VERSION}).
              </p>
            </div>
          )}
        </div>

        {/* Navegação entre passos */}
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => { setStep((s) => Math.max(0, s - 1)); setAttempted(false); }}
            disabled={step === 0}
            className="btn-secondary gap-2 disabled:opacity-0"
          >
            <ChevronLeft className="w-4 h-4" />Anterior
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={tryAdvance}
              className={cn('btn-primary gap-2', !canProceed[step] && 'opacity-70')}
            >
              Próximo<ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (!canProceed[2]) { setAttempted(true); return; }
                void handleSubmit();
              }}
              disabled={loading}
              className="btn-primary gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Criando…' : 'Criar conta'}
            </button>
          )}
        </div>

        <p className="text-center text-xs text-ink-soft mt-6">
          Ao criar a conta, seus dados são tratados conforme nossa{' '}
          <Link to="/privacidade" target="_blank" className="text-brand-700 hover:underline">
            Política de Privacidade
          </Link>.
        </p>
      </div>
    </div>
  );
}
